import React, { useContext, useEffect, useState, useCallback } from 'react'
import { Fides } from 'src/shared/js/external/fides'
import DatadogConsentHandler from './DatadogConsentHandler'
import { GoogleTagConsentHandler } from './GoogleTagConsentHandler'
import MetaConsentHandler from './MetaConsentHandler'
import TrackerConsentHandler from './TrackerConsentHandler'

declare global {
  interface Window {
    Fides: Fides | undefined
    __ETHYCA_ENABLED__: boolean | undefined
  }
}

export type CookieConsentContextType = {
  ethycaEnabled?: boolean
  ethycaInitialized: boolean
  analyticsConsent: boolean
  marketingConsent: boolean
  functionalConsent: boolean
}

const CookieConsentContext = React.createContext<CookieConsentContextType>({
  ethycaEnabled: false,
  ethycaInitialized: false,
  analyticsConsent: false,
  marketingConsent: false,
  functionalConsent: false
})

/**
 * This context is used to manage the cookie consent state.
 */
export const CookieConsentContextProvider = ({
  children
}: React.PropsWithChildren<{}>) => {
  const ethycaEnabled = window?.__ETHYCA_ENABLED__ ?? false
  const [ethycaInitialized, setEthycaInitialized] = useState(false)
  const [analyticsConsent, setAnalyticsConsent] = useState(false)
  const [marketingConsent, setMarketingConsent] = useState(false)
  const [functionalConsent, setFunctionalConsent] = useState(false)
  const handleConsentUpdate = useCallback(() => {
    setAnalyticsConsent(window?.Fides?.consent?.analytics ?? false)
    setMarketingConsent(window?.Fides?.consent?.marketing ?? false)
    setFunctionalConsent(window?.Fides?.consent?.functional ?? false)
  }, [])

  const handleEthycaInit = useCallback(() => {
    setEthycaInitialized(true)
    handleConsentUpdate()
  }, [handleConsentUpdate])

  useEffect(() => {
    if (typeof window === 'undefined') {
      // if we're in a server side render, we don't need to do anything
      return
    }
    const fides = window?.Fides

    if (fides?.initialized) {
      handleEthycaInit()
    } else {
      window.addEventListener('FidesInitialized', handleEthycaInit)
    }
    window.addEventListener('FidesUpdated', handleConsentUpdate)

    return () => {
      window.removeEventListener('FidesInitialized', handleEthycaInit)
      window.removeEventListener('FidesUpdated', handleConsentUpdate)
    }
  }, [handleConsentUpdate, handleEthycaInit])

  return (
    <CookieConsentContext.Provider
      value={{
        ethycaEnabled,
        ethycaInitialized,
        analyticsConsent,
        marketingConsent,
        functionalConsent
      }}
    >
      <DatadogConsentHandler />
      <GoogleTagConsentHandler />
      <MetaConsentHandler />
      <TrackerConsentHandler />
      {children}
    </CookieConsentContext.Provider>
  )
}

export const useCookieConsentContext = () => {
  return useContext(CookieConsentContext)
}
